import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import AddToListModal from "../AddToListModal";
import { slugify } from "../../../util/slugify";

const RestaurantActionMenu = ({ restaurant, token }) => {
  const restaurantId = restaurant._id;
  const restaurantName = restaurant.name;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 2,
      left: rect.right - 140, // align right edge with button
    });
  }, []);

  useEffect(function positionDropdownAndCloseOnClickOutside() {
    if (!open) return;
    updatePosition();
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, updatePosition]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#666",
          padding: "4px 6px",
          fontSize: "1em",
        }}
        aria-label="Restaurant actions"
      >
        <i className="fas fa-ellipsis-v" />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 10000,
              minWidth: "140px",
              overflow: "hidden",
            }}
          >
            <Link
              to={`/details/${restaurantId}/${slugify(restaurantName)}?tab=edit-listing`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              style={{
                display: "block",
                padding: "10px 14px",
                color: "#333",
                textDecoration: "none",
                fontSize: "0.9em",
                borderBottom: "1px solid #eee",
              }}
            >
              <i
                className="fas fa-pencil-alt"
                style={{ marginRight: "8px" }}
              />
              Edit
            </Link>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                // Intentionally omit googleId: a copy becomes a NEW physical
                // location, so it must not inherit the source's place ID (the
                // user picks a fresh address, which yields its own googleId).
                const { name, cuisine, veganOptions, website, status } = restaurant;
                navigate("/create/v2/address", {
                  state: {
                    name, website, status,
                    veganOptions, cuisine,
                  },
                });
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 14px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#333",
                fontSize: "0.9em",
                borderBottom: "1px solid #eee",
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: "8px" }} />
              Copy
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                setShowListModal(true);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 14px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#333",
                fontSize: "0.9em",
              }}
            >
              <i className="fas fa-plus" style={{ marginRight: "8px" }} />
              Add to List
            </button>
          </div>,
          document.body
        )}

      {showListModal && (
        <AddToListModal
          restaurantId={restaurantId}
          restaurantName={restaurantName}
          onClose={() => setShowListModal(false)}
        />
      )}
    </>
  );
};

export default RestaurantActionMenu;
